import React from "react";
import { Box, Typography, LinearProgress, Stack, Avatar, Paper, Divider } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const STATUS_MAP = {
  leadReceived: {
    title: "Lead Inquiry Received",
    subtitle: "We’ve received your request and noted your service interest.",
    order: 1
  },
  initialConsultation: {
    title: "Initial Discussion Underway",
    subtitle: "A meeting/call has been set up to discuss your needs in detail.",
    order: 2
  },
  proposalSent: {
    title: "Needs Assessment & Proposal Sent",
    subtitle: "We’ve reviewed your requirements and sent a detailed proposal/quotation.",
    order: 3
  },

  // Path A
  serviceAgreement: {
    title: "Service Agreement Confirmed",
    subtitle: "We’ve discussed and agreed on the scope, terms, and fees.",
    order: 4
  },
  paymentPending: {
    title: "Payment Pending",
    subtitle: "Invoice issued — awaiting your payment to begin work.",
    order: 5
  },
  paymentReceived: {
    title: "Payment Received",
    subtitle: "Payment confirmed — your service is now queued to start.",
    order: 6
  },
  executionInProgress: {
    title: "Service Execution In Progress",
    subtitle: "Our team is working on your service, with updates shared as we go.",
    order: 7
  },
  serviceCompleted: {
    title: "Service Completed & Delivered",
    subtitle: "Your service is complete and all deliverables have been provided.",
    order: 8
  },
  postServiceSupport: {
    title: "Post-Service Support Active",
    subtitle: "We’re here for any questions or adjustments after delivery.",
    order: 9
  },

  // Path B
  followUpSent: {
    title: "Follow-Up Sent",
    subtitle: "We’ve reached out for confirmation — awaiting your response.",
    order: 4
  },
  awaitingDecision: {
    title: "Awaiting Client Decision",
    subtitle: "We’re on standby until we hear back from you.",
    order: 5
  },
  leadDormant: {
    title: "Lead Dormant",
    subtitle: "No response after multiple attempts — case marked inactive.",
    order: 6
  }
};

const branchA = [
  "leadReceived",
  "initialConsultation",
  "proposalSent",
  "serviceAgreement",
  "paymentPending",
  "paymentReceived",
  "executionInProgress",
  "serviceCompleted",
  "postServiceSupport"
];

const branchB = [
  "leadReceived",
  "initialConsultation",
  "proposalSent",
  "followUpSent",
  "awaitingDecision",
  "leadDormant"
];

function getStepsForStatus(currentStatus) {
  let stepsToShow;
  if (branchA.includes(currentStatus)) {
    stepsToShow = branchA;
  } else if (branchB.includes(currentStatus)) {
    stepsToShow = branchB;
  } else {
    stepsToShow = branchA; // fallback to main flow
  }

  const currentOrder = STATUS_MAP[currentStatus]?.order || 0;

  return stepsToShow.map(key => {
    const step = STATUS_MAP[key];
    let status = "pending";
    if (step.order < currentOrder) status = "completed";
    else if (step.order === currentOrder) status = "inProgress";
    return { ...step, status };
  });
}

export default function ProgressTimeline({ currentStatus }) {
  const steps = getStepsForStatus(currentStatus);
  const progressValue =
    (steps.filter(s => s.status === "completed").length / steps.length) * 100;

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        border: 1,
        borderColor: "divider"
      }}
      elevation={1}
    >
      <Typography variant="h6" fontWeight="bold" mb={3}>
        Progress Timeline
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {/* Progress Bar */}
      <Box mb={4}>
        <Stack direction="row" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">
            Progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(progressValue)}% Complete
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{ height: 8, borderRadius: 4,backgroundColor: "#e0e0e0", // track color
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#60CFDB", // progress bar color
            }, }}
          
        />
      </Box>

      {/* Steps */}
      <Stack spacing={3}>
        {steps.map((step, index) => (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            key={index}
          >
            {/* Status Indicator */}
            {step.status === "completed" && (
              <Avatar
                sx={{
                  bgcolor: "#dcfce7",
                  border: "2px solid",
                  borderColor: "#22c55d",
                  width: 32,
                  height: 32
                }}
              >
                <CheckCircleIcon
                  fontSize="small"
                  sx={{ color: "#22c55d" }}
                />
              </Avatar>
            )}
            {step.status === "inProgress" && (
              <Avatar
                sx={{
                  bgcolor: "#fff",
                  border: "2px solid",
                  borderColor: "#60CFDB",
                  width: 32,
                  height: 32,
                  fontSize: 14,
                  fontWeight: "bold",
                  color: "#60CFDB"
                }}
              >
                {index + 1}
              </Avatar>
            )}
            {step.status === "pending" && (
              <Avatar
                sx={{
                  bgcolor: "grey.100",
                  border: "2px solid",
                  borderColor: "grey.300",
                  width: 32,
                  height: 32,
                  fontSize: 14,
                  fontWeight: "bold",
                  color: "grey.400"
                }}
              >
                {index + 1}
              </Avatar>
            )}

            {/* Step Details */}
            <Box>
              <Typography
                fontWeight="medium"
                color={
                  step.status === "completed"
                    ? "success.main"
                    : step.status === "inProgress"
                    ? "#60CFDB"
                    : "grey.400"
                }
              >
                {step.title}
              </Typography>
              <Typography
                variant="body2"
                color={
                  step.status === "completed"
                    ? "success.main"
                    : step.status === "inProgress"
                    ? "#60CFDB"
                    : "grey.400"
                }
              >
                {step.subtitle}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}
